import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { LocalAuthService } from './core/services/auth.service';
import { of, Subscription, switchMap } from 'rxjs';
import { TenantService } from './core/services/tenant.service';
import { LanguageService } from './core/services/language.service';
import { EnvironmentService } from './core/services/environment.service';
import { NotificationService } from './core/services/notification.service';
import { PageLoaderService } from './core/services/page-loader.service';
import { AppUpdateService } from './core/services/app-update.service';
import { LoginAuditService } from './core/services/login-audit.service';
import { NewsService } from './core/services/news.service';
import { catchError, retry } from 'rxjs/operators';
import { IonicModule } from '@ionic/angular';
import { SideMenuComponent } from './core/side-menu/side-menu.component';
import { UpdateBannerComponent } from './core/components/update-banner/update-banner.component';
import { MaintenanceBannerComponent } from './core/components/maintenance-banner/maintenance-banner.component';
import { CookieBannerComponent } from './core/components/cookie-banner/cookie-banner.component';
import { SupportWidgetComponent } from './core/components/support-widget/support-widget.component';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        IonicModule,
        SideMenuComponent,
        UpdateBannerComponent,
        MaintenanceBannerComponent,
        CookieBannerComponent,
        SupportWidgetComponent,
    ],
})
export class AppComponent implements OnInit, OnDestroy {
  private authService = inject(LocalAuthService);
  private tenantService = inject(TenantService);
  private languageService = inject(LanguageService);
  private environmentService = inject(EnvironmentService);
  private newsService = inject(NewsService);
  private pageLoaderService = inject(PageLoaderService);

  private readonly subscriptions = new Subscription();

  pageLoaderVisible = false;

  ngOnInit(): void {
    this.environmentService.applyTitle();

    this.subscriptions.add(
      this.pageLoaderService.visible$.subscribe((visible) => {
        this.pageLoaderVisible = visible;
      }),
    );

    this.subscriptions.add(
      this.authService.isAuthenticated$
        .pipe(
          switchMap((isAuthenticated) => {
            if (!isAuthenticated) {
              this.tenantService.clearRuntimeState();
              return of([]);
            }

            return this.languageService
              .loadUserLanguage()
              .pipe(
                switchMap(() => this.tenantService.loadTenants()),
                switchMap(async (tenants) => {
                  if (tenants.length > 0) {
                    await this.newsService.checkAndShowIfNeeded();
                  }
                  return tenants;
                }),
                retry({ count: 1, delay: 800 }),
                catchError((error) => {
                  console.error(
                    'Tenant bootstrap failed after token refresh attempt',
                    error,
                  );
                  return of([]);
                }),
              );
          }),
        )
        .subscribe(),
    );

  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

}
